<template>
  <Toolbar classes="pt-3">
    <template v-slot:actions>
      <div class="d-flex align-items-center">
        <router-link to="/pre-schedule-forms" class="btn btn-sm btn-light">
          <i class="bi bi-arrow-left me-1"></i>
          Voltar
        </router-link>
      </div>
    </template>
  </Toolbar>

  <div class="container-xxl">
    <!-- Filtros -->
    <div class="card mb-5">
      <div class="card-header border-bottom-0">
        <h6 class="card-title mb-0 fw-bold">
          Filtros
        </h6>
      </div>
      <div class="card-body py-4">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label fw-semibold">Profissional</label>
            <Dropdown
              :showClear="true"
              :options="professionals"
              :filter="true"
              @change="resetPage();"
              v-model="filters.professionalId"
              optionLabel="prof_name"
              optionValue="id_prof"
              id="id_prof"
              class="form-control form-control-solid"
              style="display: flex; padding: 0.35rem 0.75rem"
              placeholder="Todos os profissionais"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label fw-semibold">Data Início</label>
            <Calendar class="form-control form-control-sm form-control-solid w-full"
                      @change="resetPage();" showButtonBar
                      dateFormat="dd/mm/yy" v-model="filters.startDate"
                      placeholder="Selecione a data"/>
          </div>
          <div class="col-md-3">
            <label class="form-label fw-semibold">Data Fim</label>
            <Calendar class="form-control form-control-sm form-control-solid w-full"
                      @change="resetPage();" showButtonBar
                      dateFormat="dd/mm/yy" v-model="filters.endDate"
                      placeholder="Selecione a data"/>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-primary w-100" type="button" @click="fetchAnswers();">
              Filtrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabela de Respostas -->
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          <h6 class="mb-0 fw-bold">Respostas Recebidas</h6>
        </div>
      </div>
      <div class="card-body py-4">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
        </div>
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover table-row-bordered">
              <thead>
              <tr class="fw-bold fs-6 text-muted">
                <th>Paciente</th>
                <th>Profissional</th>
                <th>Data/Hora Agendamento</th>
                <th>Enviado em</th>
                <th>Respondido em</th>
                <th>Status</th>
                <th class="text-center">Ações</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="answer in answers.data" :key="answer.id_pre_schedule_answer">
                <td class="d-flex align-items-center">
                  <div class="d-flex flex-column">
                    <span class="text-gray-800 text-hover-primary mb-1 fw-semibold">{{ answer.patient?.patient_name }}</span>
                    <span class="text-muted fs-7">{{ answer.patient?.patient_cpf }}</span>
                  </div>
                </td>
                <td>{{ answer.schedule?.professional?.prof_name || '-' }}</td>
                <td>
                  <span v-if="answer.schedule?.sched_date && answer.schedule?.sched_time">
                    {{ formatDateTime(answer.schedule.sched_date, answer.schedule.sched_time) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span v-if="answer.schedule?.pre_schedule_sent_at">
                    {{ formatDateTimeFull(answer.schedule.pre_schedule_sent_at) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span v-if="answer.answered_at">
                    {{ formatDateTimeFull(answer.answered_at) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span class="badge" :class="answer.answered_at ? 'badge-success' : 'badge-warning'">
                    {{ answer.answered_at ? 'Respondido' : 'Pendente' }}
                  </span>
                </td>
                <td class="text-center">
                  <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                          type="button" @click="showAnswerDetails(answer)"
                          :disabled="!answer.answered_at"
                          :title="answer.answered_at ? 'Ver respostas' : 'Aguardando resposta'">
                    <i class="bi" :class="answer.answered_at ? 'bi-eye' : 'bi-eye-slash'"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!loading && answers.data && answers.data.length === 0">
                <td colspan="7" class="text-center text-muted py-5">
                  <div class="symbol symbol-50px mb-3 mx-auto">
                    <div class="symbol-label bg-light">
                      <i class="bi bi-inbox text-muted fs-2x"></i>
                    </div>
                  </div>
                  <h6 class="text-muted mb-2">Nenhuma resposta encontrada</h6>
                  <p class="text-muted fs-7 mb-0">Ajuste os filtros ou aguarde novas respostas</p>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <Pagination :data="answers" v-if="answers.data && answers.data.length > 0"/>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes da Resposta -->
    <div class="modal fade" id="modalAnswerDetails"
         tabindex="-1" aria-labelledby="modalAnswerDetails" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-light">
            <div class="d-flex align-items-center">
              <div class="symbol symbol-40px me-3">
                <div class="symbol-label bg-primary">
                  <i class="bi bi-clipboard-check fs-2 text-white"></i>
                </div>
              </div>
              <div>
                <h5 class="modal-title mb-0">Respostas do Formulário</h5>
                <span class="text-muted fs-7">Visualize as respostas do paciente</span>
              </div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <PreScheduleAnswerView
              v-if="selectedAnswer"
              :answer="selectedAnswer"
              :questions="formQuestions"
              :form-name="formInfo?.form_name"
            >
              <template #header-extra>
                <div class="col-md-6 mt-3">
                  <label class="form-label fw-semibold text-muted">Paciente</label>
                  <p class="mb-0 fw-bold">{{ selectedAnswer.patient?.patient_name }}</p>
                </div>
                <div class="col-md-6 mt-3">
                  <label class="form-label fw-semibold text-muted">Profissional</label>
                  <p class="mb-0 fw-bold">{{ selectedAnswer.schedule?.professional?.prof_name }}</p>
                </div>
                <div class="col-md-6 mt-3">
                  <label class="form-label fw-semibold text-muted">Data/Hora do Agendamento</label>
                  <p class="mb-0">
                    <span v-if="selectedAnswer.schedule?.sched_date && selectedAnswer.schedule?.sched_time">
                      {{ formatDateTime(selectedAnswer.schedule.sched_date, selectedAnswer.schedule.sched_time) }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </p>
                </div>
              </template>
            </PreScheduleAnswerView>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import Pagination from "@/components/Pagination.vue";
import http from "../../helpers/http";
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import PreScheduleAnswerView from "./PreScheduleAnswerView.vue";

export default {
  name: "PreScheduleFormAnswers",
  components: {
    Toolbar,
    Pagination,
    Dropdown,
    Calendar,
    PreScheduleAnswerView,
  },
  data() {
    return {
      filters: {
        professionalId: null,
        startDate: null,
        endDate: null,
      },
      answers: [],
      formQuestions: [],
      selectedAnswer: null,
      formId: null,
      formInfo: null,
      loading: false,
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    professionals() {
      return this.$store.state.auth.professionals;
    },
  },
  mounted() {
    this.formId = this.$route.params.formId;
    this.loadFormInfo();
    this.resetPage();
    this.fetchAnswers();
  },
  methods: {
    formatDateTime(date, time) {
      if (!date) return '-';
      const dateStr = new Date(date).toLocaleDateString('pt-BR');
      return time ? `${dateStr} às ${time}` : dateStr;
    },
    formatDateTimeFull(dateTime) {
      if (!dateTime) return '-';
      return new Date(dateTime).toLocaleString('pt-BR');
    },
    showAnswerDetails(answer) {
      if (!answer.answered_at) {
        Alert.toastWarning('Este formulário ainda não foi respondido pelo paciente.');
        return;
      }
      
      this.selectedAnswer = answer;
      Modal.toggle('#modalAnswerDetails');
    },
    loadFormInfo() {
      http.get(`/places/${this.place.id_place}/pre-schedule-forms/${this.formId}`)
        .then((response) => {
          this.formInfo = response.data;
          this.formQuestions = response.data.questions || [];
        })
        .catch((e) => {
          Alert.error(e.response?.data?.message || 'Erro ao carregar formulário');
        });
    },
    fetchAnswers() {
      this.loading = true;
      this.answers = [];
      http.get(`/places/${this.place.id_place}/pre-schedule-forms/${this.formId}/answers`, {
        params: {
          'page': this.$route.query.page,
          'per_page': 10,
          'id_prof': this.filters.professionalId,
          'start_date': this.filters.startDate,
          'end_date': this.filters.endDate,
        }
      }).then((response) => {
        this.answers = response.data;
      }).catch((e) => {
        Alert.error(e.response?.data?.message || 'Erro ao buscar respostas');
      }).finally(() => {
        this.loading = false;
      });
    },
    resetPage() {
      const query = {...this.$route.query, page: 1};
      this.$router.push({path: this.$route.path, query});
    }
  }
}
</script>

